import type { TAnyClientQuestionType } from '@hubertai/screening-question-types';
import type { TFunction } from 'i18next';
import type { TVisibilityMismatchReason } from '.';
import type { TVisibility } from 'types/template/template.types';
import type { TShareWithAccessGroupsResult } from 'types/template/templateAudience.types';

/** The question or template owning a rule: whoever is served the rule decides what it may jump to. */
export type TRuleSource = {
  kind: 'question' | 'template';
  id?: string;
  visibility: TVisibility | null | undefined;
  clientId: string | undefined;
  accessGroupIds: string[] | undefined;
};

const resolveVisibility = (visibility: TVisibility | null | undefined, clientId: string | undefined): TVisibility => {
  if (visibility === 'global' || visibility === 'client' || visibility === 'local') return visibility;
  return clientId ? 'client' : 'global';
};

const isCrossClientRuleTarget = (source: TRuleSource, target: TAnyClientQuestionType): boolean =>
  Boolean(source.clientId) &&
  resolveVisibility(target.visibility, target.client_id) !== 'global' &&
  target.client_id !== source.clientId;

export const getRuleTargetBlockReason = (
  source: TRuleSource,
  target: TAnyClientQuestionType,
  { isHubertAdmin }: { isHubertAdmin: boolean },
): TVisibilityMismatchReason => {
  if (source.id && target.id === source.id) return null;

  const sourceVisibility = resolveVisibility(source.visibility, source.clientId);
  const targetVisibility = resolveVisibility(target.visibility, target.client_id);

  if (targetVisibility === 'global') return null;
  if (sourceVisibility === 'global') return 'visibilityMismatch';
  if (isCrossClientRuleTarget(source, target)) return isHubertAdmin ? 'differentClient' : 'visibilityMismatch';
  if (sourceVisibility === 'client') return targetVisibility === 'local' ? 'visibilityMismatch' : null;
  if (targetVisibility !== 'local') return null;

  const sourceGroups = source.accessGroupIds ?? [];
  if (!sourceGroups.length) return null;
  const sharesAGroup = sourceGroups.some((id) => (target.access_groups_ids ?? []).includes(id));
  return sharesAGroup ? null : 'missingAccessGroups';
};

export const getRuleTargetShareGroupIds = (
  source: TRuleSource,
  reason: TVisibilityMismatchReason,
  { isHubertAdmin, memberGroupIds }: { isHubertAdmin: boolean; memberGroupIds: string[] },
): string[] => {
  if (reason !== 'missingAccessGroups') return [];
  const ownerGroupIds = source.accessGroupIds ?? [];
  return isHubertAdmin ? ownerGroupIds : ownerGroupIds.filter((id) => memberGroupIds.includes(id));
};

type TShareRefusal = Extract<TShareWithAccessGroupsResult, { shared: false }>['reason'];

/** The text for a refused share. Groups of another organization are worded as a plain failure. */
export const getRuleTargetShareFailureKey = (
  reason: TShareRefusal | undefined,
): Exclude<TShareRefusal, 'other_client_groups'> | 'unknown' =>
  reason === undefined || reason === 'other_client_groups' ? 'unknown' : reason;

export const getRuleTargetTooltipContent = (t: TFunction<'Questionsv2'>, reason: TVisibilityMismatchReason): string => {
  switch (reason) {
    case 'visibilityMismatch':
      return t('searchQuestion.ruleTargetTooltip.visibilityMismatch');
    case 'missingAccessGroups':
      return t('searchQuestion.ruleTargetTooltip.missingAccessGroups');
    case 'differentClient':
      return t('searchQuestion.ruleTargetTooltip.differentClient');
    case null:
      return '';
  }
};
